/**
 * 清溪天成模版 - VIP会员页
 * 
 * 设计：脉冲光环Hero + 卡片旋转排列 + 特色功能网格
 * 独特排版：Hero区使用动画脉冲光环（不同于传统的渐变Hero），
 * 套餐卡片略微旋转错位排列（不同于整齐的并排），功能展示使用大图标网格
 */

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/templates/crystal/components/Navbar";
import Footer from "@/templates/crystal/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import { getVipPlans } from "@/utils/adminApi";

interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem("current_site_id") || "";
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: { Authorization: token ? `Bearer ${token}` : "", "X-Site-Id": siteId },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch {
    return null;
  }
}

export default function CrystalVipPage() {
  const { isLoggedIn } = useAuth();

  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<number>(1);
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [activeFeatureIdx, setActiveFeatureIdx] = useState(0);

  useEffect(() => {
    Promise.all([
      getVipPlans().then(setPackages).catch(() => {}),
      fetchPaymentConfig().then(setPaymentConfig).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleBuy = useCallback(
    (pkg: any) => {
      if (!isLoggedIn) {
        window.location.href = `/login?redirect=/vip`;
        return;
      }
      setSelectedPlan(pkg.id);
      setShowPayModal(true);
    },
    [isLoggedIn]
  );

  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);
  const currentPkg = displayPackages.find((p: any) => p.id === selectedPlan) || displayPackages[0];

  // 特色功能
  const features = [
    { icon: "ri-send-plane-fill", title: "无限发布", desc: "不受限制，随心发布信息" },
    { icon: "ri-rocket-fill", title: "置顶推荐", desc: "每日自动刷新置顶" },
    { icon: "ri-verified-badge-fill", title: "官方认证", desc: "专属V标，权威可信" },
    { icon: "ri-image-add-fill", title: "20张图片", desc: "高清多角度展示" },
    { icon: "ri-bar-chart-fill", title: "数据统计", desc: "详细浏览数据" },
    { icon: "ri-customer-service-fill", title: "专属客服", desc: "优先响应通道" },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeatureIdx((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [features.length]);

  const displayPackages =
    packages.length > 0
      ? packages
      : [
          { id: 1, name: "月度VIP", price: "29.9", duration: 30, description: "适合短期需求", is_recommended: false },
          { id: 2, name: "季度VIP", price: "79.9", duration: 90, description: "性价比之选", is_recommended: true },
          { id: 3, name: "年度VIP", price: "239.9", duration: 365, description: "最高性价比", is_recommended: false },
        ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero：脉冲光环 */}
      <section className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-b from-cyan-50/80 via-white to-white">
        {/* 脉冲光环 */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-64 h-64 md:w-96 md:h-96 rounded-full border-2 border-cyan-300/20 animate-ping" style={{ animationDuration: "4s" }} />
          <div className="absolute w-48 h-48 md:w-72 md:h-72 rounded-full border-2 border-cyan-300/30 animate-ping" style={{ animationDuration: "3s", animationDelay: "0.5s" }} />
          <div className="absolute w-32 h-32 md:w-48 md:h-48 rounded-full border-2 border-cyan-300/40 animate-ping" style={{ animationDuration: "2s", animationDelay: "1s" }} />
          <div className="absolute w-16 h-16 md:w-24 md:h-24 rounded-full bg-cyan-400/10 flex items-center justify-center">
            <i className="ri-vip-crown-line text-2xl md:text-3xl text-cyan-500" />
          </div>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-100/60 rounded-full text-cyan-700 text-xs font-semibold mb-6">
            <i className="ri-diamond-line" /> VIP 会员特权
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            升级<span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">VIP</span>，解锁无限可能
          </h1>
          <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            尊享置顶推荐、无限发布、官方认证等专属特权
          </p>
        </div>
      </section>

      {/* 特色功能网格 */}
      <section className="pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
            {features.map((feat, idx) => {
              const isActive = idx === activeFeatureIdx;
              return (
                <div
                  key={idx}
                  className={`p-5 rounded-2xl border transition-all duration-500 ${
                    isActive
                      ? "bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200 shadow-lg shadow-cyan-500/5 scale-[1.02]"
                      : "bg-white border-gray-100 hover:border-cyan-100 hover:shadow-md"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 transition-all ${
                      isActive ? "bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-md shadow-cyan-500/25" : "bg-cyan-50 text-cyan-600"
                    }`}
                  >
                    <i className={`${feat.icon} text-sm`} />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm mb-1">{feat.title}</h3>
                  <p className="text-xs text-gray-400">{feat.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 套餐卡片：旋转错位排列 */}
      <section className="pb-16 bg-cyan-50/20 pt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">选择套餐</h2>
            <p className="text-gray-400 text-sm">选择适合您的VIP方案</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-3xl border border-gray-100 p-8">
                  <div className="w-1/2 h-5 bg-gray-100 rounded mx-auto mb-4" />
                  <div className="w-1/3 h-10 bg-gray-100 rounded mx-auto mb-6" />
                  <div className="space-y-3">
                    {[...Array(4)].map((_, j) => (
                      <div key={j} className="w-full h-4 bg-gray-100 rounded" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 max-w-4xl mx-auto">
              {displayPackages.map((pkg, idx) => {
                const isRecommended = pkg.is_recommended;
                return (
                  <div
                    key={pkg.id}
                    className={`relative bg-white rounded-3xl border-2 transition-all duration-300 w-full md:w-64 ${
                      isRecommended
                        ? "border-cyan-400 shadow-xl shadow-cyan-500/15 scale-105 z-20"
                        : "border-gray-100 shadow-md hover:border-cyan-200 hover:shadow-lg"
                    }`}
                    style={{ transform: isRecommended ? undefined : idx === 0 ? "rotate(-1deg)" : idx === 2 ? "rotate(1deg)" : undefined }}
                  >
                    {isRecommended && (
                      <div className="absolute -top-3.5 inset-x-4 flex justify-center">
                        <span className="px-5 py-1.5 bg-gradient-to-r from-cyan-400 to-blue-500 text-white text-xs font-bold rounded-full shadow-lg shadow-cyan-500/30 flex items-center gap-1.5">
                          <i className="ri-fire-line text-[10px]" /> 最受欢迎
                        </span>
                      </div>
                    )}

                    <div className="p-6 md:p-7">
                      <div className="text-center mb-5">
                        <h3 className="text-base font-bold text-gray-800 mb-1">{pkg.name}</h3>
                        <p className="text-xs text-gray-400">{pkg.description}</p>
                      </div>

                      <div className="text-center mb-5">
                        <div className="flex items-baseline justify-center gap-0.5">
                          <span className="text-sm text-gray-400">¥</span>
                          <span className={`text-3xl font-extrabold ${isRecommended ? "text-cyan-600" : "text-gray-800"}`}>
                            {pkg.price}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {pkg.duration >= 365 ? `仅 ¥${(Number(pkg.price) / 12).toFixed(1)}/月` : `共 ${pkg.duration} 天`}
                        </p>
                      </div>

                      <ul className="space-y-2.5 mb-7">
                        {["无限发布信息", "每日置顶推荐", "官方VIP认证", "20张高清图片", "专属客服通道"].map((f, fi) => (
                          <li key={fi} className="flex items-center gap-2">
                            <i className={`text-xs ${isRecommended ? "ri-checkbox-circle-fill text-cyan-500" : "ri-check-line text-green-500"}`} />
                            <span className="text-xs text-gray-600">{f}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handleBuy(pkg)}
                        className={`w-full py-3 rounded-full font-bold text-sm transition-all ${
                          isRecommended
                            ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25 hover:from-cyan-600 hover:to-blue-700"
                            : "border-2 border-cyan-200 text-cyan-600 hover:bg-cyan-50"
                        }`}
                      >
                        立即开通
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-10 text-center">
            <p className="text-xs text-gray-400">
              开通即表示同意{" "}
              <Link to="/page/register-agreement" className="text-cyan-500 hover:text-cyan-600">服务协议</Link>
              {" "}和{" "}
              <Link to="/page/privacy" className="text-cyan-500 hover:text-cyan-600">隐私政策</Link>
            </p>
          </div>
        </div>
      </section>

      <Footer />

      {/* 支付弹窗 */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-800 text-lg">扫码支付</h3>
              <button
                onClick={() => setShowPayModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              {showWechat && (
                <button
                  onClick={() => setPayMethod("wechat")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all ${
                    payMethod === "wechat" ? "bg-green-50 text-green-700 border-2 border-green-400" : "bg-gray-50 text-gray-500 border-2 border-transparent hover:border-gray-200"
                  }`}
                >
                  <i className="ri-wechat-pay-fill" />微信
                </button>
              )}
              {showAlipay && (
                <button
                  onClick={() => setPayMethod("alipay")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all ${
                    payMethod === "alipay" ? "bg-blue-50 text-blue-700 border-2 border-blue-400" : "bg-gray-50 text-gray-500 border-2 border-transparent hover:border-gray-200"
                  }`}
                >
                  <i className="ri-alipay-fill" />支付宝
                </button>
              )}
            </div>

            <div className="text-center">
              {payMethod === "alipay" && showAlipay && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">使用支付宝扫码付款</p>
                  <img src={paymentConfig?.alipay_qrcode} alt="支付宝" className="w-52 h-52 mx-auto rounded-xl border border-gray-200" />
                </div>
              )}
              {payMethod === "wechat" && showWechat && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">使用微信扫码付款</p>
                  <img src={paymentConfig?.wechat_qrcode} alt="微信" className="w-52 h-52 mx-auto rounded-xl border border-gray-200" />
                </div>
              )}
              {!showAlipay && !showWechat && (
                <>
                  <div className="w-44 h-44 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
                    <i className="ri-qr-code-line text-5xl text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">支付功能暂未启用</p>
                  <p className="text-xs text-gray-400">请联系管理员配置收款码</p>
                </>
              )}
              <p className="text-2xl font-bold text-cyan-600 mt-4 mb-1">¥{currentPkg?.price || "29.9"}</p>
              <p className="text-xs text-gray-400">{currentPkg?.name || "VIP套餐"} · {currentPkg?.duration || 30}天</p>
            </div>

            <div className="mt-5 p-3.5 bg-cyan-50 rounded-xl text-xs text-cyan-700 text-center font-medium">
              <i className="ri-information-line mr-1" />支付成功后将自动开通VIP权益
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
